import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Download, 
  Play, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  Clock,
  BookOpen,
  Target,
  Zap
} from 'lucide-react';
import { runFullQuizAudit, getQuickAuditStats, type FullAuditReport } from '@/scripts/runFullQuizAudit';
import { toast } from 'sonner';

const QuizAuditReport = () => {
  const [auditReport, setAuditReport] = useState<FullAuditReport | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [quickStats, setQuickStats] = useState(getQuickAuditStats());

  useEffect(() => {
    // Update quick stats on mount
    setQuickStats(getQuickAuditStats());
  }, []);

  const handleRunAudit = () => {
    setIsRunning(true);
    toast.info('Starting comprehensive quiz audit...');
    
    try {
      const report = runFullQuizAudit();
      setAuditReport(report);
      setQuickStats(getQuickAuditStats());
      toast.success(`Audit complete! Analyzed ${report.summary.totalCourses} courses in ${report.executionTime}ms`);
    } catch (error) {
      console.error('Audit failed:', error);
      toast.error('Audit failed. Check console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleExportJSON = () => {
    if (!auditReport) return;
    
    const json = JSON.stringify(auditReport, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-audit-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Audit report exported to JSON');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'good': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'needs-improvement': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'critical': return 'bg-destructive/10 text-destructive border-destructive/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'destructive';
      case 'HIGH': return 'default';
      case 'MEDIUM': return 'secondary';
      case 'LOW': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Quiz Coverage Audit</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive analysis of quiz coverage across all 60+ course types
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleRunAudit}
            disabled={isRunning}
            className="gap-2"
          >
            <Play className="h-4 w-4" />
            {isRunning ? 'Running Audit...' : 'Run Full Audit'}
          </Button>
          {auditReport && (
            <Button onClick={handleExportJSON} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export JSON
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Coverage</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{quickStats.coverage}%</div>
            <Progress value={quickStats.coverage} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {quickStats.totalModules - quickStats.missingCount} of {quickStats.totalModules} modules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{quickStats.quality}%</div>
            <Progress value={quickStats.quality} className="mt-2" />
            <Badge variant="outline" className="mt-2">
              {quickStats.quality >= 90 ? 'Excellent' : quickStats.quality >= 70 ? 'Good' : 'Needs Work'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Missing Quizzes</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{quickStats.missingCount}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Modules requiring quiz content
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge variant={quickStats.status === 'excellent' ? 'default' : 'secondary'} className="mt-1">
              {quickStats.status.toUpperCase()}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">
              Overall system health
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Report */}
      {auditReport && (
        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="priority">Priority Actions</TabsTrigger>
            <TabsTrigger value="course-types">Course Types</TabsTrigger>
            <TabsTrigger value="missing">Missing Quizzes</TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Executive Summary</CardTitle>
                <CardDescription>
                  Generated {new Date(auditReport.timestamp).toLocaleString()} in {auditReport.executionTime}ms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Total Courses</span>
                      <span className="text-sm text-muted-foreground">{auditReport.summary.totalCourses}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Total Modules</span>
                      <span className="text-sm text-muted-foreground">{auditReport.summary.totalModules}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Modules with Quizzes</span>
                      <span className="text-sm text-muted-foreground">{auditReport.summary.modulesWithQuizzes}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Modules without Quizzes</span>
                      <span className="text-sm font-destructive">{auditReport.summary.modulesWithoutQuizzes}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Coverage Percentage</span>
                      <Badge variant="outline">{auditReport.summary.coveragePercentage}%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Quality Grade</span>
                      <Badge>{auditReport.summary.qualityGrade}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Quality Score</span>
                      <span className="text-sm text-muted-foreground">{auditReport.summary.overallQualityScore}%</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Coverage Progress</h4>
                  <Progress value={auditReport.summary.coveragePercentage} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Priority Actions Tab */}
          <TabsContent value="priority" className="space-y-4">
            {auditReport.priorityActions.map((action, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={getPriorityColor(action.priority)}>
                        {action.priority}
                      </Badge>
                      <CardTitle className="text-base">{action.action}</CardTitle>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span className="text-xs">{action.estimatedTime}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    <strong>Impact:</strong> {action.impact}
                  </p>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Course Types Tab */}
          <TabsContent value="course-types" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(auditReport.courseTypeBreakdown)
                .sort(([, a]: any, [, b]: any) => b.quizCoverage - a.quizCoverage)
                .map(([type, data]: any) => (
                  <Card key={type}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm uppercase">{type}</CardTitle>
                        <Badge className={getStatusColor(data.status)}>
                          {data.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Quiz Coverage</span>
                          <span className="font-medium">{data.quizCoverage.toFixed(1)}%</span>
                        </div>
                        <Progress value={data.quizCoverage} className="h-1" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Quality Score</span>
                          <span className="font-medium">{data.averageQuality.toFixed(1)}%</span>
                        </div>
                        <Progress value={data.averageQuality} className="h-1" />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <BookOpen className="h-3 w-3" />
                        <span>{data.coursesCount} courses</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </TabsContent>

          {/* Missing Quizzes Tab */}
          <TabsContent value="missing" className="space-y-4">
            {(['critical', 'high', 'medium', 'low'] as const).map(priority => {
              const items = auditReport.missingQuizzesByPriority[priority];
              if (items.length === 0) return null;

              return (
                <Card key={priority}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Badge variant={getPriorityColor(priority.toUpperCase())}>
                        {priority.toUpperCase()}
                      </Badge>
                      <CardTitle className="text-base">
                        {items.length} Missing {items.length === 1 ? 'Quiz' : 'Quizzes'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {items.slice(0, 20).map((item, index) => (
                        <div key={index} className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {item.courseType}
                              </Badge>
                              <code className="text-xs">{item.moduleId}</code>
                            </div>
                            <p className="text-xs text-muted-foreground">{item.reason}</p>
                          </div>
                        </div>
                      ))}
                      {items.length > 20 && (
                        <p className="text-xs text-muted-foreground text-center pt-2">
                          + {items.length - 20} more modules
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>
      )}

      {/* No Report Yet */}
      {!auditReport && !isRunning && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Audit Report Available</h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
              Click "Run Full Audit" to analyze quiz coverage across all courses and generate a comprehensive report.
            </p>
            <Button onClick={handleRunAudit} className="gap-2">
              <Play className="h-4 w-4" />
              Run Full Audit
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuizAuditReport;
